import React, { useState } from 'react';
import { Button } from '../../../atoms/button/button';
import { Dialog } from '../../../atoms/dialog/dialog';
import { List } from '../../../molecules/list/list';
import { LoadingWithText } from '../../../atoms/loading-with-text/loading-with-text';
import { Menu, menuItems } from '../../../atoms/menu/menu';
import { SelectOption } from '../../../atoms/select/select';
import { Spacing, Typography } from '../../../atoms/progress-with-label/progress-with-label';
import { Tabs } from '../../../molecules/tabs/tabs';
import styled from '@emotion/styled';
import {
  ExamTypes,
  formatDate,
  GeneratedExamStatus,
  IExam,
} from '@taleemabad/db';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { SelectedOptions } from '@taleemabad/providers';

export interface ExamInterface extends IExam {
  section: string;
  checkpointName?: string | null;
  selectedOptions?: SelectedOptions;
  isUGExam?: boolean;
}

interface ExamGeneratorClassListProps {
  exams: ExamInterface[] | undefined;
  handleExamClick: (exam: ExamInterface) => void;
  createNewExam: () => void;
  showNoExams: boolean;
  isSyncing: boolean;
  activeTab: number;
  handleTabChange: (value: number) => void;
  handleAssessmentFilter: (value: string) => void;
  selectedCheckpoint?: string;
  checkpoints?: SelectOption[];
  menuData: menuItems[];
  onMenuItemClick?: (value: string, exam: ExamInterface) => void;
}

const TABS = [
  {
    key: 1,
    label: 'Classroom Practice Test',
    dataCy: 'exam-generator-exam-history-tab-classroom-practice-test',
  },
  {
    key: 2,
    label: 'Exam',
    dataCy: 'exam-generator-exam-history-tab-term-assessment',
  },
];

const filterAndSortExams = (
  exams: ExamInterface[],
  statuses: GeneratedExamStatus[],
) => {
  return exams
    ?.filter((exam) => statuses.includes(exam.status))
    .sort(
      (a, b) =>
        new Date(b.created ?? '').getTime() -
        new Date(a.created ?? '').getTime(),
    );
};

const ExamSection: React.FC<{
  title: string;
  exams: ExamInterface[];
  handleExamClick: (exam: ExamInterface) => void;
  getExamStatus: (status: GeneratedExamStatus) => string;
  openMenu: string | null;
  activeTab?: number;
  setOpenMenu: (value: string | null) => void;
  menuData: menuItems[];
  setConfirmationDialog: (value: boolean) => void;
  setSelectedExam: (exam: ExamInterface | null) => void;
}> = ({
  title,
  exams,
  handleExamClick,
  getExamStatus,
  openMenu,
  activeTab,
  setOpenMenu,
  menuData,
  setConfirmationDialog,
  setSelectedExam,
}) => (
  <>
    {activeTab == 1 && (
      <Spacing marginBottom="md">
        <Typography variant="textTitleSmallOnSurfaceVariant">
          {title}
        </Typography>
      </Spacing>
    )}
    {exams.map((exam) => {
      const isAIGeneratedExam = exam.type === 'AI_GENERATED_EXAM';
      const isUGExam =
        exam.type === ExamTypes.ChapterAssessment ||
        exam.type === ExamTypes.TermExam;
      const supportingText =
        isUGExam || isAIGeneratedExam
          ? exam.selectedOptions?.examType?.content || ''
          : `Chapter ${exam.selectedOptions?.selectedChapters
              ?.map((chapter) => chapter.chapterNumber)
              .join(', ')}`;

      const sectionSuffix = exam.section ? ' - ' + exam.section : '';
      const overlineText =
        (exam.selectedOptions?.grade?.content ?? '') +
        sectionSuffix +
        ' ' +
        (exam.selectedOptions?.subject?.content ?? '');

      return (
        <Spacing
          key={exam.id}
          style={{ position: 'relative' }}
          marginBottom="lg"
        >
          <List
            alignment="center"
            overlineText={overlineText}
            heading={exam.checkpointName}
            supportingText={supportingText}
            trailingContainerDirection={
              exam.type === ExamTypes.ChapterAssessment ? 'row' : 'column'
            }
            trailingSupportingText={formatDate(exam.created ?? '')}
            onClick={() => handleExamClick(exam)}
            {...(exam.type !== ExamTypes.ChapterAssessment
              ? {
                  trailingIcon: {
                    direction: 'trailing',
                    type: 'badge',
                    badgeText: getExamStatus(exam.status),
                    badgeColor: 'var(--md-sys-color-on-surface-variant)',
                    badgeSize: 'large',
                  },
                }
              : {
                  trailingIcon: {
                    direction: 'trailing',
                    source: 'more_vert',
                    name: 'more_vert',
                    type: 'icon',
                    cursor: 'pointer',
                  },
                  onTrailingIconClick: () => {
                    setOpenMenu(openMenu === exam.id ? null : exam.id);
                  },
                  trailingIconDataCy: 'exam-generator-3dot-menu',
                })}
          />
          <Menu
            positioning="absolute"
            xOffset={40}
            yOffset={-13}
            open={openMenu === exam.id}
            menuCorner="end-end"
            anchorCorner="end-end"
            menuItems={menuData}
            onSelectedItem={(data) => {
              setOpenMenu(null);
              if (data.id === 1) {
                setConfirmationDialog(true);
                setSelectedExam(exam);
              }
            }}
            onClose={() => {
              setOpenMenu(null);
            }}
          />
        </Spacing>
      );
    })}
  </>
);

export const ExamGeneratorHistory: React.FC<ExamGeneratorClassListProps> = ({
  exams,
  handleExamClick,
  createNewExam,
  showNoExams,
  isSyncing,
  activeTab = 1,
  handleTabChange,
  handleAssessmentFilter,
  selectedCheckpoint = 'All Assessments',
  checkpoints = [],
  menuData,
  onMenuItemClick,
}) => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [selectedExam, setSelectedExam] = useState<ExamInterface | null>(null);

  const onDeleteExam = (exam: ExamInterface) => {
    if (onMenuItemClick) {
      onMenuItemClick('delete', exam);
    }
    setShowConfirmationDialog(false);
    setSelectedExam(null);
  };

  const getExamStatus = (status: GeneratedExamStatus): string => {
    switch (status) {
      case GeneratedExamStatus.Uploading:
        return 'Generating...';
      case GeneratedExamStatus.ReadyForUpload:
        return 'Ongoing';
      case GeneratedExamStatus.ReadyForChecking:
      case GeneratedExamStatus.CheckingInProgress:
      case GeneratedExamStatus.Checked:
        return 'Conducted';
      default:
        return '';
    }
  };

  // Filter by status AND by exam type (based on active tab)
  const getExamsByTabAndStatus = (
    examList: ExamInterface[],
    statuses: GeneratedExamStatus[],
  ) => {
    const tabExamType =
      activeTab === 1 ? ExamTypes.ChapterAssessment : ExamTypes.TermExam;
    return filterAndSortExams(examList, statuses).filter(
      (exam) => exam.type === tabExamType,
    );
  };

  const ongoingExams = getExamsByTabAndStatus(exams ?? [], [
    GeneratedExamStatus.Uploading,
    GeneratedExamStatus.ReadyForUpload,
  ]);

  const conductedExams = getExamsByTabAndStatus(exams ?? [], [
    GeneratedExamStatus.ReadyForChecking,
    GeneratedExamStatus.CheckingInProgress,
    GeneratedExamStatus.Checked,
  ]);

  return (
    <Container>
      <Spacing marginTop="md" marginBottom="xl">
        <Typography
          variant="textTitleMediumSurfaceVariant"
          data-cy="exam-generator-exam-history-title"
        >
          Exam History
        </Typography>
      </Spacing>
      <Spacing marginBottom="xl">
        <Tabs
          variant="secondary"
          activeTabKey={activeTab}
          tabs={TABS}
          onChange={handleTabChange}
        />
      </Spacing>
      {/* Hidden: checkpoint filter only needed for legacy Term Exam flow
      {activeTab === 1 && (
        <Spacing marginBottom="xl">
          <Select
            label="Select Assessment"
            defaultValue={selectedCheckpoint}
            options={checkpoints}
            variant="outlined"
            width="60%"
            onChange={(selected) => handleAssessmentFilter(selected.value)}
          />
        </Spacing>
      )}
      */}
      {showNoExams && !isSyncing ? (
        <NoExamsContainer>
          <Typography variant="textTitleMediumSurfaceVariant">
            No exam created yet.
          </Typography>
        </NoExamsContainer>
      ) : (
        <>
          {ongoingExams && ongoingExams.length > 0 && (
            <ExamSection
              title="Ongoing Assessments"
              exams={ongoingExams}
              handleExamClick={handleExamClick}
              getExamStatus={getExamStatus}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              activeTab={activeTab}
              menuData={menuData}
              setConfirmationDialog={setShowConfirmationDialog}
              setSelectedExam={setSelectedExam}
            />
          )}

          {conductedExams && conductedExams.length > 0 && (
            <ExamSection
              title="Already Conducted Assessments"
              exams={conductedExams}
              handleExamClick={handleExamClick}
              getExamStatus={getExamStatus}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              activeTab={activeTab}
              menuData={menuData}
              setConfirmationDialog={setShowConfirmationDialog}
              setSelectedExam={setSelectedExam}
            />
          )}
        </>
      )}

      <Footer>
        <Button
          icon="Add"
          variant="filled"
          onClick={createNewExam}
          data-cy="create-exam-button"
        >
          Create Exam
        </Button>
        {isSyncing && <LoadingWithText message="" />}
      </Footer>

      <Dialog
        open={showConfirmationDialog}
        title="Delete Exam"
        onClose={() => {
          setShowConfirmationDialog(false);
          setSelectedExam(null);
        }}
      >
        <Typography variant="textBodyMedium">
          Do you want to delete this Classroom Practice Test?{' '}
        </Typography>

        <StyledPopupButton>
          <Button
            variant="text"
            onClick={() => {
              setShowConfirmationDialog(false);
              setSelectedExam(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="delete-outlined"
            onClick={() => selectedExam && onDeleteExam(selectedExam)}
          >
            Delete
          </Button>
        </StyledPopupButton>
      </Dialog>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-bottom: var(--rk-spacing-xxxxxxxl);
  ${mq(BreakpointSizes.xs)} {
    margin-bottom: 0;
    padding-bottom: var(--rk-spacing-xxxxxxxxxxl);
  }
`;

const NoExamsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const Footer = styled.div`
  position: fixed;
  width: 100%;
  bottom: 0;
  padding: var(--rk-spacing-xl) 0;
  background-color: var(--md-ref-neutral-100);
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${mq(BreakpointSizes.xs)} {
    bottom: 80px;
    padding: var(--rk-spacing-lg) 0;
  }
`;
const StyledPopupButton = styled.div`
  margin-top: var(--rk-spacing-lg);
  display: flex;
  justify-content: space-between;
  gap: var(--rk-spacing-md);
  md-filled-button {
    width: 50%;
  }
  md-outlined-button {
    width: 50%;
  }
  md-text-button {
    width: 50%;
  }
`;
